import { css } from "@emotion/css";
import debounce from "lodash/debounce";
import {
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { Trans } from "react-i18next";

import { Checkbox, Text } from "@certn/cascadia-react";

import { FormFeedback } from "../../components/inputs/_assets/input-wrapper/FormFeedback";
import { Link } from "../../components/link/Link";
import { useMediaQuery } from "../../components/utils";
import { useColors } from "../../hooks/useColors";
import { useScrollbarStyles } from "../../hooks/useScrollbarStyles";

type Props = {
  label?: string;
  presignedUrl: string;
  errorMessage?: string;
  isSigned?: boolean;
  onChange?: (isSelected: boolean) => void;
  skipHasToScroll?: boolean;
};

export function CertnDocumentScroll({
  label,
  presignedUrl,
  isSigned,
  onChange,
  errorMessage,
  skipHasToScroll = false,
}: PropsWithChildren<Props>) {
  const styles = useStyles();
  const contentRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [hasToScroll, setHasToScroll] = useState<boolean>(false);
  const [hasUserScrolledToBottom, setHasUserScrolledToBottom] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isPdfLoaded, setIsPdfLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!presignedUrl) {
      setIsError(true);
      setIsLoading(false);
      return;
    }

    setIsError(false);
    setIsLoading(true);
    setIsPdfLoaded(false);
    setHasUserScrolledToBottom(false);
    setHasToScroll(!skipHasToScroll);
  }, [presignedUrl, skipHasToScroll]);

  const checkScrollability = useCallback(() => {
    if (!contentRef.current || skipHasToScroll || !isPdfLoaded) {
      return;
    }

    const element = contentRef.current;
    const hasScrollableContent = element.scrollHeight > element.clientHeight;

    if (!hasScrollableContent) {
      setHasUserScrolledToBottom(true);
      setHasToScroll(false);
    } else if (!hasUserScrolledToBottom) {
      setHasToScroll(true);
    }
  }, [skipHasToScroll, isPdfLoaded, hasUserScrolledToBottom]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setIsPdfLoaded(true);
    setTimeout(checkScrollability, 500);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!isPdfLoaded || skipHasToScroll || hasUserScrolledToBottom) {
      return;
    }

    const element = e.target as HTMLDivElement;
    const isNearBottom =
      element.scrollHeight <= element.scrollTop + element.clientHeight + 50;

    if (isNearBottom) {
      setHasUserScrolledToBottom(true);
      setHasToScroll(false);
    }
  };

  useEffect(() => {
    if (!contentRef.current || !isPdfLoaded) return;

    const debouncedCheck = debounce(() => {
      checkScrollability();
    }, 200);

    const observer = new ResizeObserver(() => {
      debouncedCheck();
    });

    observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
      debouncedCheck.cancel();
    };
  }, [checkScrollability, isPdfLoaded]);

  return (
    <div>
      <section
        className={css(styles.section, {
          borderColor: errorMessage ? "var(--border-danger-input)" : undefined,
        })}
      >
        <header className={styles.header}>
          <Link
            testID="certn-contract-download-link"
            href={presignedUrl}
            target="_blank"
            textColor="text-weak"
            textStyle="global/body-secondary"
          >
            <Trans>Download</Trans>
          </Link>
        </header>

        <div
          ref={contentRef}
          className={styles.content}
          aria-label="contract-content"
          role="textbox"
          tabIndex={0}
          onScroll={handleScroll}
        >
          {isLoading && !isPdfLoaded && (
            <Text color="text-weak" testID="certn-contract-loading">
              <Trans>Loading document...</Trans>
            </Text>
          )}
          {isError && (
            <Text color="text-weak" testID="certn-contract-fetch-error">
              <Trans>Something went wrong</Trans>
            </Text>
          )}
          {!isError && presignedUrl && (
            <iframe
              ref={iframeRef}
              src={`${presignedUrl}#toolbar=0&navpanes=0`}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="PDF Document"
              onLoad={handleIframeLoad}
            />
          )}
        </div>

        <footer
          className={css(styles.footer, {
            textAlign: hasToScroll ? "center" : "left",
          })}
        >
          {!isError &&
            isPdfLoaded &&
            (hasToScroll ? (
              <Text testID="certn-please-scroll-text">
                <Trans>Please scroll to the bottom to accept</Trans>
              </Text>
            ) : (
              <Checkbox
                isSelected={isSigned}
                onChange={onChange}
                isDisabled={isSigned}
                testID="certn-contract-scroll"
              >
                {label || (
                  <Trans>I understand and agree to this document</Trans>
                )}
              </Checkbox>
            ))}
        </footer>
      </section>
      {errorMessage && (
        <FormFeedback type="invalid">{errorMessage}</FormFeedback>
      )}
    </div>
  );
}

function useStyles() {
  const { mediaQuery } = useMediaQuery();
  const colors = useColors();
  const scrollbarStyles = useScrollbarStyles();

  return {
    section: css(
      {
        backgroundColor: "var(--background-base)",
        width: "100%",
        height: "320px",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${colors["border-base"]}`,
        borderRadius: "var(--border-radius-3)",
      },
      mediaQuery({
        maxWidth: ["unset", "580px"],
      })
    ),
    header: css(
      {
        textAlign: "right",
      },
      mediaQuery({
        padding: [
          `${"var(--space-3)"} ${"var(--space-4)"}`,
          `${"var(--space-3)"} ${"var(--space-5)"}`,
        ],
      })
    ),
    footer: css(
      mediaQuery({
        padding: [
          `${"var(--space-2)"} ${"var(--space-4)"}`,
          `${"var(--space-2)"} ${"var(--space-5)"}`,
        ],
      })
    ),
    content: css(
      {
        flex: 1,
        borderTop: `1px solid ${colors["border-base"]}`,
        borderBottom: `1px solid ${colors["border-base"]}`,
        overflow: "auto",
        // text color for document
        "& *": {
          color: `${colors["text-weak"]} !important`,
        },
      },
      scrollbarStyles,
      mediaQuery({
        padding: ["var(--space-4)", `${"var(--space-4)"} ${"var(--space-5)"}`],
      })
    ),
  };
}
